import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import { toggleLoading } from "../../../redux";
import {UtilService} from "../../../shared/services";
import {Store} from "@ngrx/store";
import {ToastrService} from "ngx-toastr";
import {UuidService} from "../../../../services";
import {SubdocService} from "../../services/subdoc/subdoc.service";
import {Router, UrlTree} from "@angular/router";
import {IEntityDTO, ISignatoryDTO, ISubdocAccessPerson, ISubdocRequest, PageStatus} from "../../models/syndication";
import { DataService } from 'src/app/services/data/data.service';
import { EntitiesForSubDocsModel, KycSignatoryForSubDocModel, SubDocsPersonModel } from '../../models/entities-for-subdoc';
import { ModalSize } from 'src/app/modules/shared/services/util/util.service';
import { DealSignatoryAddPersonModalComponent } from '../deal-signatory-add-person-modal/deal-signatory-add-person-modal.component';

@Component({
  selector: 'app-deal-subscription-subdoc-modal',
  templateUrl: './deal-subscription-subdoc-modal.component.html',
  styleUrls: ['./deal-subscription-subdoc-modal.component.scss']
})
export class DealSubscriptionSubdocModalComponent implements OnInit {

  fg: FormGroup = new FormGroup({
    amount: new FormControl(0, [Validators.required, Validators.min(1)]),
    businessUnitId: new FormControl(),
    subscriptionId: new FormControl(),
    signOffPersonName: new FormControl(),
  })

  businessUnitEntities: EntitiesForSubDocsModel[] = [];
  selectedBusinessUnitEntity?: EntitiesForSubDocsModel;
  selectedSignatory?: KycSignatoryForSubDocModel;
  dealSignatories: SubDocsPersonModel[] = [];

  pageStatus = PageStatus.EDITING;
  protected readonly PageStatus = PageStatus;

  constructor(
    public util: UtilService,
    private store: Store,
    private uuid: UuidService,
    private toastrService: ToastrService,
    private subDocService: SubdocService,
    private dataService: DataService,
    private router: Router,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<DealSubscriptionSubdocModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { businessUnitId: string, subscriptionId: string, amount: number }) {
  }

  ngOnInit(): void {
    this.fg.patchValue(this.data);
    this.getEntitiesForBusinessUnit(this.data.businessUnitId);
  }

  getEntitiesForBusinessUnit(businessUnitId: string) {
    console.log("Fetching Entities...");
    this.store.dispatch(toggleLoading({loading: true}));
    this.subDocService.getSubdocEntities(businessUnitId).subscribe({
      next: (entities: IEntityDTO[]) => {
        this.businessUnitEntities = entities;
        this.selectedBusinessUnitEntity = undefined;
        this.store.dispatch(toggleLoading({loading: false}));
      }, error: err => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastrService.error("Unable to get entities!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }

  addSelectedSignatory() {
    if (!this.selectedSignatory) {
      this.toastrService.error("Please select a signatory, or add a new one!");
      return;
    }

    if (this.dealSignatories.length >= 2) {
      this.toastrService.warning("You can have a minimum of 1 and maximum of 2 signers.");
      return;
    }

    this.dialog.open(DealSignatoryAddPersonModalComponent, {
      width: this.util.getModalWidth(ModalSize.SMALL),
      data: {
        fullName: this.selectedSignatory.name,
        email: ''
      }
    })
    .afterClosed().subscribe(result => {
      if (!!result) {
        this.dealSignatories.push(<SubDocsPersonModel> {
          id: this.uuid.generateFormattedUUID(),
          businessUnitEntityKycPersonId: this.selectedSignatory?.businessUnitEntityKycAuthorizedSignatoryId,
          fullName: result.fullName,
          email: result.email,
          sortOrder: this.dealSignatories.length + 1
        });
      }
    });
  }
  
  addNonKYCSignatory() {
    if (this.dealSignatories.length >= 2) {
      this.toastrService.warning("You can have a minimum of 1 and maximum of 2 signers.");
      return;
    }

    this.dialog.open(DealSignatoryAddPersonModalComponent, {
      width: this.util.getModalWidth(ModalSize.SMALL),
    })
    .afterClosed().subscribe(result => {
      if (!!result) {
        // let signatories: any[] = this.form.get(this.FORM_SIGNATORIES)?.value || [];
        this.dealSignatories.push(<SubDocsPersonModel> {
          id: this.uuid.generateFormattedUUID(),
          businessUnitEntityKycPersonId: null,
          fullName: result.fullName,
          email: result.email,
          sortOrder: 1
        });
      }
    });
  }
  
  getErrorMsg() : string {
    return this.selectedBusinessUnitEntity?.kycErrorMessage ?? '';
  }

  removeSignatory(signatory: SubDocsPersonModel) {
    this.dealSignatories = this.dealSignatories.filter(x => x.id != signatory.id);
  }

  openKyc() {
    if (!this.selectedBusinessUnitEntity) {
      this.toastrService.error("Could not resolve entity id");
      return;
    }

    // if there is KYC Id then send user to the KYC, else send user to the entity
    var baseUrl = window.location.origin;
    let route: UrlTree;
    if (!!this.selectedBusinessUnitEntity.businessUnitEntityKycId) {
      // has KYC
      route = this.router.createUrlTree([this.dataService.getBuSlug(), 'entities', this.selectedBusinessUnitEntity.entityId, 'kyc', this.selectedBusinessUnitEntity.businessUnitEntityKycId]);
    }
    else {
      // no KYC
      route = this.router.createUrlTree([this.dataService.getBuSlug(), 'entities', this.selectedBusinessUnitEntity.entityId, 'kyc']);
    }

    var combinedUrl = baseUrl + route.toString();

    setTimeout(() => {
      window.open(combinedUrl, '_blank');
    }, 500);
  }

  initSubDoc() {
    console.log("Initialising Sub Doc...");
    // check that there is emails
    if (this.dealSignatories.length == 0) {
      this.toastrService.error("At least one signatory must be added for the Sub Doc to be initiated");
      return;
    }

     if (this.dealSignatories.length > 2) {
      this.toastrService.warning("You can have a minimum of 1 and maximum of 2 signers.");
      return;
    }

    var nullEmails = this.dealSignatories.filter(x => !x.email);

    if (nullEmails.length > 0) {
      this.toastrService.error(`You must enter an email address for signer '${nullEmails[0].fullName}'`);
      return;
    }

    if (!this.selectedBusinessUnitEntity) {
      this.toastrService.error("You must select an investing entity from the dropdown");
      return;
    }

    if (!this.selectedBusinessUnitEntity?.businessUnitEntityKycId) {
      this.toastrService.error("You must select an investing entity with an approved KYC");
      return;
    }

    var payload = <ISubdocRequest> {
      amount: this.fg.getRawValue().amount,
      subscriptionId: this.fg.value.subscriptionId,
      signOffPersonName: this.fg.value.signOffPersonName,
      businessUnitKycId: this.selectedBusinessUnitEntity?.businessUnitEntityKycId,
      investingEntityId: this.selectedBusinessUnitEntity?.entityId,
      investorSubdocAccessPersons: this.dealSignatories
    }

    console.log("PAYLOAD", payload);

    this.store.dispatch(toggleLoading({loading: true, message: "This operation may take a few seconds!"}));
    this.subDocService.addSubdoc(this.dataService.getDealId(), payload).subscribe({
      next: (res: any) => {
        this.pageStatus = PageStatus.COMPLETE;
        this.store.dispatch(toggleLoading({loading: false}));
        // this.dialogRef.close(payload);
      }, error: err => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastrService.error("Unable to initialise sub doc!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }
}

